import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import ReplayIcon from '@mui/icons-material/Replay';
import { MuiOtpInput } from 'mui-one-time-password-input';
import KeywordSearchDropdown from './KeywordSearchDropdown';
import IRequestFormData from '../models/IRequestFormData';
import { IKeyword } from '../models/IKeyword';
import userTypesData from '../assets/AthenaUserTypes2.json';
import NPSDegreeData from '../assets/NPSDegrees.json';
import AthenaServiceTypes from '../assets/AthenaServiceTypes.json';
import NPSDepartments from '../assets/NPSDepartments.json';
import AthenaRankTypes from '../assets/AthenaRankTypes.json';
import {
  submitFormData,
  sendOTP,
  verifyOTP,
  mergeAthenaUsers,
  addAthenaUser,
  getAthenaKeywords,
} from '../api/athena4p-api';
import npsfLogoWhite from '../assets/npsf-logo-white.png';
import IRequestSubmissionData from '../models/IRequestSubmissionData';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { CountryData } from 'react-phone-input-2';
import INPSDegrees from '../models/INPSDegrees';
import INPSDepartments from '../models/INPSDepartments';
import IAthenaRankTypes from '../models/IAthenaRankTypes';
import IAthenaServiceTypes from '../models/IAthenaServiceTypes';
// import { IAthenaUniversity } from '../models/IAthenaUniversity';
// import { IAthenaNation } from '../models/IAthenaNation';
import AthenaUniversities from '../assets/AthenaUniversities.json';
import AthenaNations from '../assets/AthenaNations.json';
import IAddUsers from '../models/IAddUsers';
import '../styles/RequestForm.css';
import GroupForm from './GroupForm';
import { IMatchingUsersData } from '../models/IRequestAccount';
import cachedKeywordsData from '../assets/keywords.json';

type Severity = 'error' | 'success' | 'info' | 'warning' | undefined;

const RequestForm: React.FC = () => {
  const [formData, setFormData] = useState<IRequestFormData>({
    firstName: '',
    middleName: '',
    lastName: '',
    maidenName: '',
    email: '',
    phoneNumber: '',
    password: '',
    userType: '',
    isAlumni: false,
    nodeTypeId: 0,
    researchInterests: [],
    achieveGoal: '',
    sendCopy: false,
    department: '',
    organization: '',
    rank: '',
    course: '',
    otherService: '',
    nationality: '',
    universityCollege: [],
    DepartmentId: 0,
    GraduateId: 0,
    recommendedBy: '',
  });
  const [additionalFields, setAdditionalFields] = useState<any[]>([]);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [recommendedByError, setRecommendedByError] =
    useState<boolean>(false);
  const [researchInterestsError, setResearchInterestsError] =
    useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [degreeOptions, setDegreeOptions] = useState<string[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [serviceOptions, setServiceOptions] = useState<string[]>([]);
  const [rankOptions, setRankOptions] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [otp, setOtp] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState<string>('');
  const [formSubmissionData, setFormSubmissionData] =
    useState<IRequestSubmissionData>({} as IRequestSubmissionData);
  // Suppress TypeScript warning - formSubmissionData will be used when real API is integrated
  void formSubmissionData;
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [alertSeverity, setAlertSeverity] = useState<Severity>('info');
  const [userSubmitted, setUserSubmitted] = useState<boolean>(false);
  const [addingUser, setAddingUser] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string>('');
  const [isAlumni, setIsAlumni] = useState<boolean | null>(null);
  const [selectedTabValue, setSelectedTabValue] = React.useState(1);
  const [duplicateUserFound, setDuplicateUserFound] =
    React.useState<boolean>(false);

  // State variables for pre-approved user duplicate handling
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [existingUsers, setExistingUsers] = useState<IMatchingUsersData[]>([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState<string[]>([]);
  const [keywordsData, setKeywordsData] = useState<any[]>([]);
  const [approvedUser, setApprovedUser] = useState<boolean>(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);

  useEffect(() => {
    loadKeywordsData();
  }, []);

  useEffect(() => {
    if (userSubmitted && approvedUser) {
      setRedirectCountdown(5);

      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.href = '/athena';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [userSubmitted, approvedUser]);

  const loadKeywordsData = async () => {
    try {
      const response = await getAthenaKeywords();

      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}`);
      }

      if (!Array.isArray(response.data) || !response.data.length) {
        throw new Error('Keywords data is empty or invalid');
      }

      setKeywordsData(response.data);
    } catch (error) {
      console.error('Keywords fetch failed:', error);
      setKeywordsData(cachedKeywordsData);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTabValue(Number(newValue));
  };

  const passwordRef = useRef<any>(null);
  const emailRef = useRef<any>(null);
  const phoneNumberRef = useRef<any>(null);
  const gradSchoolsRef = useRef<any>(null);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    const recommendedByPattern =
      /^[A-Za-z][A-Za-z' -]*,\s*[A-Za-z][A-Za-z' -]*(\s+[A-Za-z][A-Za-z' -]*)*$/;

    if (name === 'email') {
      const restrictedDomains = ['@athena4p.org', '@npsalumni.org'];
      const isRestrictedEmail = restrictedDomains.some((domain) =>
        value.toLowerCase().endsWith(domain.toLowerCase())
      );

      if (isRestrictedEmail) {
        setEmailError(true);
        setEmailErrorMessage(
          'Registration with this email domain is not allowed. Please use your official organization email address.'
        );
      } else {
        setEmailError(false);
        setEmailErrorMessage('');
      }
    }

    if (name === 'recommendedBy') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        setRecommendedByError(false);
      } else {
        setRecommendedByError(!recommendedByPattern.test(trimmedValue));
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (value: string, fieldTitle: string) => {
    const name =
      fieldTitle && fieldTitle.includes('/')
        ? replaceSlashes(fieldTitle)
        : replaceSpaces(fieldTitle);
    setFormData({ ...formData, [name]: value });

    if (fieldTitle === 'Organization/Service' && value === 'Other') {
      setFormData({ ...formData, otherService: '' });
    } else if (fieldTitle === 'Organization/Service') {
      setFormData({ ...formData, organization: value });
    } else if (fieldTitle === 'Rank') {
      setFormData({ ...formData, rank: value });
    }
  };

  const handleServiceChange = (value: string) => {
    const selectedService = AthenaServiceTypes.find(
      (service: IAthenaServiceTypes) => service.Title === value
    );

    const useServiceId = selectedService ? selectedService.UseServiceId : 0;

    if (useServiceId === 0) {
      setRankOptions([]);
    } else {
      const rankOptions = AthenaRankTypes.filter(
        (rank: IAthenaRankTypes) => rank.ServiceTypeId === useServiceId
      )
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((rank: IAthenaRankTypes) => rank.RankTitle);
      setRankOptions(rankOptions);
    }

    setFormData({ ...formData, organization: value });
  };

  const handleChipChange = (fieldTitle: string, selectedItems: string[]) => {
    setSelectedChips({ ...selectedChips, [fieldTitle]: selectedItems });
    const name = fieldTitle.includes('/')
      ? replaceSlashes(fieldTitle)
      : replaceSpaces(fieldTitle);
    setFormData({ ...formData, [name]: selectedItems.join('||') });
  };

  const handleGradSchoolsChange = (_event: any, selectedSchools: any) => {
    if (selectedSchools) {
      setSelectedSchools(selectedSchools);
      setFormData({ ...formData, universityCollege: selectedSchools });
    }
  };

  const getSelectedKeywords = (keywords: IKeyword[]) => {
    setFormData({ ...formData, researchInterests: keywords });
    if (keywords && keywords.length > 0) {
      setResearchInterestsError(false);
    }
  };

  const replaceSpaces = (str: string) => {
    return str
      .toLowerCase()
      .replace(/(?:^|\s)(.)/g, (_, char) => char.toUpperCase())
      .replace(/\s+/g, '')
      .replace(/^./, (str) => str.toLowerCase());
  };

  const replaceSlashes = (str: string) => {
    return str
      .toLowerCase()
      .replace(/\/(.)/g, (_: any, char: any) => char.toUpperCase());
  };

  const handleDropdownChange = (event: SelectChangeEvent<string>) => {
    const userType = event.target.value;
    setFormData({ ...formData, userType });

    const selectedUserType = userTypesData.find(
      (type: any) => type.Title === userType
    );
    const userTypeId = selectedUserType ? selectedUserType.UserTypeId : 0;

    const fields = userTypesData.filter(
      (type: any) => type.ParentId === userTypeId
    );
    setAdditionalFields(fields);

    if (fields.some((field) => field.Title === 'Degree(s)')) {
      const degreeOptions = NPSDegreeData.map(
        (degree: INPSDegrees) => degree.GraduateProgramTypeLongName
      ).sort((a, b) => a.localeCompare(b));
      setDegreeOptions(degreeOptions);
    } else {
      setDegreeOptions([]);
    }

    if (
      fields.some(
        (field) =>
          field.Title === 'Organization/Service' ||
          field.Title === 'Organization' ||
          field.Title === 'Current Organization/Service'
      )
    ) {
      const serviceOptions = AthenaServiceTypes.filter(
        (service: IAthenaServiceTypes) => service.DisplayToUser
      )
        .map((service: IAthenaServiceTypes) => service.Title)
        .sort((a, b) => a.localeCompare(b));
      setServiceOptions(serviceOptions);
    } else {
      setServiceOptions([]);
    }

    if (fields.some((field) => field.Title === 'Rank')) {
      const rankOptions = AthenaRankTypes.filter(
        (rank: IAthenaRankTypes) => rank.ServiceTypeId !== 0
      )
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((rank: IAthenaRankTypes) => rank.RankTitle);
      setRankOptions(rankOptions);
    } else {
      setRankOptions([]);
    }

    if (
      fields.some(
        (field) =>
          field.Title === 'Department(s)' || field.Title === 'Department'
      )
    ) {
      const departmentOptions = NPSDepartments.map(
        (department: INPSDepartments) => department.Description
      ).sort((a, b) => a.localeCompare(b));
      setDepartmentOptions(departmentOptions);
    } else {
      setDepartmentOptions([]);
    }
  };

  const handlePhoneNumberChange = (value: string, data: CountryData) => {
    const dialCode = data.dialCode;
    if (!value && formSubmitted) {
      setIsValid(false);
      return;
    } else {
      if (dialCode) {
        const phn = value.slice(dialCode.length);
        if (!phn && formSubmitted) {
          setIsValid(false);
          return;
        }
      }
    }
    const phoneNumber = `+${value}`;
    setIsValid(true);
    setFormData({ ...formData, phoneNumber: phoneNumber });
  };

  const renderAdditionalFields: any = () => {
    return additionalFields.map((field, index) => {
      const isRequired =
        field.ParentId === 0 ? true : field.Required !== undefined;

      if (
        field.Title === 'Most Recent Alma Mater' ||
        field.Title === 'University/College' ||
        field.Title === 'Graduate School(s)'
      ) {
        return (
          <Autocomplete
            key={index}
            onChange={handleGradSchoolsChange}
            clearIcon={false}
            options={AthenaUniversities.map((uni) => uni.Title)}
            freeSolo
            multiple
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...otherProps } = getTagProps({ index });
                return <Chip key={key} label={option} {...otherProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                ref={gradSchoolsRef}
                required={selectedSchools.length === 0}
                {...params}
                key={index}
                label={field.Title}
                helperText='Type the school name and press enter key to add'
              />
            )}
          />
        );
      } else if (field.Title === 'Degree(s)') {
        return (
          <Autocomplete
            key={index}
            onChange={(_event: any, value: any) =>
              handleChipChange(field.Title, value)
            }
            multiple
            disablePortal
            options={degreeOptions}
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={
                  !selectedChips[field.Title] ||
                  selectedChips[field.Title].length === 0
                }
                helperText='Type to search or add new degree...'
              />
            )}
          />
        );
      } else if (
        field.Title === 'Organization/Service' ||
        field.Title === 'Organization' ||
        field.Title === 'Current Organization/Service'
      ) {
        return (
          <React.Fragment key={index}>
            <Autocomplete
              onChange={(_event: any, value: any) => {
                handleServiceChange(value);
              }}
              disablePortal
              options={serviceOptions}
              freeSolo
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.Title}
                  required={isRequired}
                  helperText='Type to search or add new service...'
                />
              )}
            />
            {formData.organization === 'Other' && (
              <TextField
                label='Please specify your service organization'
                name='otherService'
                fullWidth
                required={isRequired}
                margin='normal'
                variant='outlined'
                onChange={handleInputChange}
              />
            )}
          </React.Fragment>
        );
      } else if (field.Title === 'Rank') {
        return (
          <React.Fragment key={index}>
            {formData.organization && (
              <Autocomplete
                freeSolo
                onInputChange={(_event, value) => {
                  setFormData({ ...formData, rank: value }); // Capture custom input
                }}
                options={rankOptions} // Existing options
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Rank'
                    required
                    helperText='Type to search or add new rank...'
                  />
                )}
              />
            )}
          </React.Fragment>
        );
      } else if (
        field.Title === 'Department(s)' ||
        field.Title === 'Department'
      ) {
        return (
          <Autocomplete
            key={index}
            onChange={(_event: any, value: any) =>
              handleChipChange(field.Title, value)
            }
            disablePortal
            options={departmentOptions}
            multiple
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={
                  !selectedChips[field.Title] ||
                  selectedChips[field.Title].length === 0
                }
                helperText='Type to search or add new department...'
              />
            )}
          />
        );
      } else if (
        field.Title === 'Start Date' ||
        field.Title === 'Expected Graduation Date' ||
        field.Title === 'Most Recent Graduation Date' ||
        field.Title === 'Graduation Date'
      ) {
        const name =
          field.Title && field.Title.includes('/')
            ? replaceSlashes(field.Title)
            : replaceSpaces(field.Title);
        return (
          <TextField
            key={index}
            label={field.Title}
            name={name}
            type='date'
            // type='number'
            // inputProps={{ min: 1900, max: new Date().getFullYear() }}
            fullWidth
            required={isRequired}
            margin='normal'
            variant='outlined'
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={
              field.Title === 'Start Date'
                ? 'Please enter the year you started'
                : 'Please enter your expected graduation year'
            }
          />
        );
      } else if (field.Title === 'Nationality') {
        return (
          <Autocomplete
            key={index}
            onChange={(_event: any, value: any) => {
              handleSelectChange(value, field.Title);
            }}
            disablePortal
            options={AthenaNations.map((nation) => nation.Title)}
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={isRequired}
                helperText='Type to search or add new nationality...'
              />
            )}
          />
        );
      } else {
        return (
          <TextField
            key={index}
            label={field.Title}
            name={
              field.Title && field.Title.includes('/')
                ? replaceSlashes(field.Title)
                : replaceSpaces(field.Title)
            }
            fullWidth
            required={isRequired}
            margin='normal'
            variant='outlined'
            onChange={handleInputChange}
          />
        );
      }
    });
  };

  const formSubmission = async (e: any) => {
    if (e) {
      e.preventDefault();
    }
    setFormSubmitted(true);

    const restrictedDomains = ['@athena4p.org', '@npsalumni.org'];
    const isRestrictedEmail = restrictedDomains.some((domain) =>
      formData.email.toLowerCase().endsWith(domain.toLowerCase())
    );

    if (isRestrictedEmail) {
      setEmailError(true);
      setEmailErrorMessage(
        'Registration with this email domain is not allowed. Please use your official organization email address.'
      );
      emailRef.current && emailRef.current.focus();
      return;
    }

    const recommendedByPattern =
      /^[A-Za-z][A-Za-z' -]*,\s*[A-Za-z][A-Za-z' -]*(\s+[A-Za-z][A-Za-z' -]*)*$/;
    const trimmedRecommendedBy = formData.recommendedBy?.trim();
    if (
      trimmedRecommendedBy &&
      !recommendedByPattern.test(trimmedRecommendedBy)
    ) {
      setRecommendedByError(true);
      return;
    }
    setRecommendedByError(false);

    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const passwordError =
      formData.password && regex.test(formData.password) ? false : true;
    setPasswordError(passwordError);

    if (passwordError) {
      passwordRef.current && passwordRef.current.focus();
      return;
    }

    if (!isValid) {
      alert('Enter valid phone number');
      phoneNumberRef.current && phoneNumberRef.current.focus();
      return;
    }

    if (
      !formData.researchInterests ||
      formData.researchInterests.length === 0
    ) {
      setResearchInterestsError(true);
      return;
    }

    setResearchInterestsError(false);

    const userType = userTypesData.find((userType) => {
      if (userType.Title === formData.userType) {
        return userType;
      }
    });

    formData.nodeTypeId = userType ? userType.NodeTypeId : 0;

    let gradSchools: string = '';
    if (Array.isArray(formData.universityCollege)) {
      gradSchools = formData.universityCollege.join('||');
    } else if (typeof formData.universityCollege === 'string') {
      gradSchools = formData.universityCollege;
    } else {
      gradSchools = '';
    }

    const keywordIds = formData.researchInterests
      .map((k) => k.keywordId)
      .join(' ');
    const keywordsText = formData.researchInterests
      .map((k) => k.title)
      .join(';');

    const formSubmissionData: IRequestSubmissionData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      password: formData.password,
      userType: formData.userType,
      isAlumni: formData.isAlumni,
      nodeTypeId: formData.nodeTypeId,
      researchInterests: formData.researchInterests,
      achieveGoal: formData.achieveGoal,
      sendCopy: formData.sendCopy,
      DepartmentId: formData.DepartmentId,
      GraduateId: formData.GraduateId,
      keywordIds: keywordIds,
      keywordsText: keywordsText,
    };

    formSubmissionData.middleName = formData.middleName;
    formSubmissionData.maidenName = formData.maidenName;
    formSubmissionData.achieveGoal = formData.achieveGoal;
    formSubmissionData.gradSchools = gradSchools;
    formSubmissionData.deptOfStudy = formData.department;
    formSubmissionData.graduateDegreeProgram = formData.degree;
    formSubmissionData.rotationDate =
      formData.expectedGraduationDate ?? formData.startDate;
    formSubmissionData.citizenship = formData.nationality;
    formSubmissionData.service = formData.organizationService;
    formSubmissionData.rank = formData.rank;
    formSubmissionData.jobTitle = formData.jobDescription;
    formSubmissionData.currentOrganization = formData.organization;

    if (formData.otherService) {
      formSubmissionData.otherService = formData.otherService;
    }
    formSubmissionData.recommendedBy = formData.recommendedBy;
    await sendOtpToUser();
    setDialogOpen(true);
    setFormSubmissionData(formSubmissionData);
  };

  const resetFormData = () => {
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      maidenName: '',
      email: '',
      phoneNumber: '',
      password: '',
      userType: '',
      isAlumni: false,
      nodeTypeId: 0,
      researchInterests: [],
      achieveGoal: '',
      sendCopy: false,
      department: '',
      organization: '',
      course: '',
      otherService: '',
      nationality: '',
      universityCollege: [],
      DepartmentId: 0,
      GraduateId: 0,
      recommendedBy: '',
    } as IRequestFormData);
  };

  const addUserToDb = async () => {
    setAddingUser(true);

    try {
      const usersData: IAddUsers = {
        users: [formSubmissionData],
      };

      const userResponseDataResponse = await submitFormData(usersData);

      if (userResponseDataResponse && userResponseDataResponse.status === 200) {
        const userResponseData = userResponseDataResponse.data;

        // Extract fields
        const { rowKey, ExternalUserId, ApprovedUser, ExistingUsers } =
          userResponseData;

        formSubmissionData.rowKey = rowKey;
        formSubmissionData.ExternalUserId = ExternalUserId;

        setFormSubmissionData(formSubmissionData);
        setApprovedUser(ApprovedUser);

        // Check conditions
        if (ApprovedUser === true && ExistingUsers.length === 0) {
          // Call addToDb API for new approved users
          // This is handled in backend.
          setUserSubmitted(true);
          setFirstName(formData.firstName);
          resetFormData();
        } else if (ApprovedUser === true && ExistingUsers.length > 0) {
          // Show duplicate modal for pre-approved users
          setExistingUsers(ExistingUsers);
          setShowDuplicateModal(true);
          setAddingUser(false);
        } else {
          // Handle case where user is not pre-approved or has no existing duplicates
          setUserSubmitted(true);
          setAddingUser(false);
          setFirstName(formData.firstName);
          resetFormData();
        }
      } else if (
        userResponseDataResponse &&
        userResponseDataResponse.status === 409
      ) {
        setDuplicateUserFound(true);
      }
    } catch (error) {
      console.error('Error in addUserToDb:', error);
      const status = (error as any)?.response?.status;
      if (status === 409) {
        setDuplicateUserFound(true);
      }
      setAddingUser(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setOtp('');
  };

  const handleChange = (newValue: string) => {
    setOtp(newValue);
  };

  // Handle duplicate user selection is now handled by simple checkboxes

  // Handle checkbox selection for duplicate users
  const handleCheckboxChange = (userId: string, checked: boolean) => {
    const currentSelection = selectedDuplicates || [];
    if (checked) {
      // Add to selection if not already present
      if (!currentSelection.includes(userId)) {
        setSelectedDuplicates([...currentSelection, userId]);
      }
    } else {
      // Remove from selection
      setSelectedDuplicates(currentSelection.filter((id) => id !== userId));
    }
  };

  // Handle cancel/none selection
  const handleCancelDuplicateSelection = async () => {
    // Continue with normal account creation
    try {
      setShowDuplicateModal(false);
      setAddingUser(true);
      setFirstName(formSubmissionData.firstName);
      await addAthenaUser(formSubmissionData);

      setShowDuplicateModal(false);
      setSelectedDuplicates([]);
      setExistingUsers([]);

      setUserSubmitted(true);
      setAddingUser(false);

      setTimeout(() => {
        resetFormData();
      }, 100);
    } catch (error) {
      console.error('Error in handleSubmitDuplicateSelections:', error);
      setAddingUser(false);
      setShowDuplicateModal(false);
    }
  };

  // Handle submit duplicate selections
  const handleSubmitDuplicateSelections = async () => {
    setAddingUser(true);

    try {
      // Stage 1: Save critical data BEFORE any resets
      const savedFirstName = formData.firstName;

      if (selectedDuplicates && selectedDuplicates.length) {
        await mergeAthenaUsers(
          formSubmissionData,
          existingUsers.filter((user) => selectedDuplicates.includes(user.id))
        );
      } else {
        await addAthenaUser(formSubmissionData);
      }

      setShowDuplicateModal(false);
      setSelectedDuplicates([]);
      setExistingUsers([]);

      setUserSubmitted(true);
      setFirstName(savedFirstName);
      setAddingUser(false);

      setTimeout(() => {
        resetFormData();
      }, 100);
    } catch (error) {
      console.error('Error in handleSubmitDuplicateSelections:', error);
      setAddingUser(false);
      setShowDuplicateModal(false);
    }
  };

  const sendOtpToUser = async () => {
    const sendOTPData = {
      email: formData.email,
    };

    await sendOTP(sendOTPData)
      .then((sendOTPResponse) => {
        if (sendOTPResponse && sendOTPResponse.status === 200) {
          setAlertSeverity('success');
          setOtpStatusMessage(
            `OTP sent to ${formData.email}.If not received,please check your SPAM folder before resending.`
          );
        }
      })
      .catch(() => {
        setAlertSeverity('error');
        setOtpStatusMessage(`Failed to send OTP to ${formData.email}`);
      });
  };

  const handleResendClick = async () => {
    await sendOtpToUser();
  };

  const handleOTPSubmitClick = async () => {
    const verifyOTPData = {
      email: formData.email,
      otp: otp,
    };

    await verifyOTP(verifyOTPData)
      .then(async (verifyOTPResponse) => {
        if (verifyOTPResponse && verifyOTPResponse.status === 200) {
          setOtpStatusMessage(`Sucessfully verified OTP`);
          setTimeout(() => {}, 3000);
          setOtpVerified(true);
          setAlertSeverity('success');
          addUserToDb();
        }
      })
      .catch((error) => {
        if (error.response) {
          setAlertSeverity('error');
          setOtpStatusMessage(
            error.response.data ?? 'Something went wrong.Please try again!'
          );
        }
      });
  };

  const handleTryAgainButtonClick = () => {
    resetStates();
  };

  const resetStates = () => {
    setUserSubmitted(false);
    setOtpVerified(false);
    setOtpStatusMessage('');
    setFormSubmitted(false);
    setDialogOpen(false);
    setAlertSeverity('info');
    setPasswordError(false);
    setOtp('');
  };

  const welcomeText = () => {
    return (
      <Stack spacing={3}>
        <Typography variant='h5'>{`Hello ${firstName},`}</Typography>
        <Typography variant='h5'>Welcome to Athena!</Typography>
        {approvedUser ? (
          <>
            <Typography variant='h6'>
              Your account has been approved! Please download the Athena desktop
              application to get started.
            </Typography>
            <Typography variant='body1' color='primary'>
              Redirecting in {redirectCountdown} second
              {redirectCountdown !== 1 ? 's' : ''}...
            </Typography>
          </>
        ) : (
          <Typography variant='h6'>
            You will be receiving an email with login instructions in the next
            few hours. Please be sure to check your SPAM folder so it is not
            missed!
          </Typography>
        )}
        <Typography variant='h6'>
          Thanks,<br></br> The Athena Team
        </Typography>
      </Stack>
    );
  };

  // const OTPForm = () => { // Unused function
  const DuplicateUsersModal = () => {
    // Defensive checks for existingUsers data following RequestFormOld.tsx patterns
    const safeExistingUsers = existingUsers || [];
    const safeSelectedDuplicates = selectedDuplicates || [];

    return (
      <Dialog open={showDuplicateModal} fullWidth maxWidth='lg'>
        <DialogTitle>
          Please select any records below that match your previous work.
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, width: '100%' }}>
            {safeExistingUsers.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          indeterminate={
                            safeSelectedDuplicates.length > 0 &&
                            safeSelectedDuplicates.length <
                              safeExistingUsers.length
                          }
                          checked={
                            safeExistingUsers.length > 0 &&
                            safeSelectedDuplicates.length ===
                              safeExistingUsers.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDuplicates(
                                safeExistingUsers.map((user) => user.id)
                              );
                            } else {
                              setSelectedDuplicates([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Document Title
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Author Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Date Completed
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Research Source
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Advisor(s)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeExistingUsers.map((user, index) => (
                      <TableRow
                        key={user.id || `row-${index}`}
                        hover
                        sx={{ '&:hover': { backgroundColor: 'grey.50' } }}
                      >
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={safeSelectedDuplicates.includes(user.id)}
                            onChange={(e) =>
                              handleCheckboxChange(user.id, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell>{user.Title || 'N/A'}</TableCell>
                        <TableCell>{user.Authors || 'N/A'}</TableCell>
                        <TableCell>{user.PublishedDate || 'N/A'}</TableCell>
                        <TableCell>
                          {user.ResearchSourceName || 'N/A'}
                        </TableCell>
                        <TableCell>{user.Advisors || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant='body2' color='textSecondary'>
                No matching records found.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDuplicateSelection} color='secondary'>
            None
          </Button>
          <Button
            onClick={handleSubmitDuplicateSelections}
            variant='contained'
            color='primary'
            disabled={addingUser}
          >
            {addingUser ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const OTPForm = () => {
    return (
      <Dialog open={dialogOpen}>
        <Box display='flex' alignItems='center'>
          <Box flexGrow={1}>
            {' '}
            <DialogTitle align='center'>Verify OTP</DialogTitle>{' '}
          </Box>
          <Box>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent>
          <Stack spacing={3}>
            <Typography>
              An One-Time-Password (OTP) is sent to your registered email please
              verify to continue.
            </Typography>
            <MuiOtpInput
              value={otp}
              onChange={handleChange}
              length={6}
              autoFocus
            />
            {otpStatusMessage && (
              <Alert severity={alertSeverity}>{otpStatusMessage}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendClick} color='secondary'>
            Resend
          </Button>
          <Button
            onClick={handleOTPSubmitClick}
            color='primary'
            variant='contained'
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const addUserDialog = () => {
    return (
      <Dialog
        open={true}
        fullWidth
        sx={{
          minHeight: '600px',
          maxHeight: '600px',
        }}
      >
        <DialogContent sx={{ minHeight: '200px' }}>
          {addingUser ? (
            // Loading state
            <Stack alignItems={'center'} sx={{ paddingTop: '70px' }}>
              <CircularProgress color='info' />
              <Typography variant='h6'>Please wait...</Typography>
            </Stack>
          ) : showDuplicateModal ? (
            // Duplicate modal is showing - keep showing loading or a message
            <Stack alignItems={'center'} sx={{ paddingTop: '70px' }}>
              <Typography variant='h6'>
                Processing duplicate records...
              </Typography>
            </Stack>
          ) : userSubmitted ? (
            // Success state
            <Stack spacing={5}>
              <Typography>{welcomeText()}</Typography>
            </Stack>
          ) : (
            // Error state - only show if truly an error

            <Stack>
              <Typography variant='h3'>
                {duplicateUserFound ? (
                  <Stack spacing={2}>
                    <Typography variant='h5'>
                      Duplicate Account Found
                    </Typography>
                    <Typography>
                      An account with this email already exists. If you believe
                      this is an error, please contact support at
                      athena@npsfoundation.org.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography variant='h5'>Submission Failed</Typography>
                    <Typography>
                      Sorry, something went wrong. Please try again or contact
                      support at athena@npsfoundation.org.
                    </Typography>
                  </Stack>
                )}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!addingUser && !showDuplicateModal && !userSubmitted && (
            <Button
              variant='outlined'
              startIcon={<ReplayIcon />}
              onClick={handleTryAgainButtonClick}
            >
              Try again
            </Button>
          )}
          {userSubmitted && (
            <Button variant='contained' startIcon={<HomeIcon />} href='/'>
              Home
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    // Prevent Enter key from submitting the form unless it's the submit button
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  return (
    <div id='request-form-root'>
      <div className='blue-section'>
        <img
          src={npsfLogoWhite}
          alt='NPSF Logo'
          style={{ width: '150px', height: 'auto', marginBottom: '1rem' }}
        />
        <h1>ATHENA for Partners</h1>
        <h2>Account Request Form</h2>
        <p>
          If you or your company would like to be a part of the problem-solving
          network of the Naval Postgraduate School and defense innovation
          ecosystem, fill out the form below to register your interest and
          request an Athena for Partners account.
        </p>
        <p style={{ fontWeight: 600 }}>Existing User?</p>
        <p>
          We've upgraded Athena. If you have an existing account, use the link
          below to move to the new platform.
        </p>
        <Button
          component={Link}
          to='/athena/migrate-account'
          size='small'
          sx={{ mt: 1, mb: 2 }}
        >
          Migrate your account here
        </Button>
        <p>
          Athena for Partners extends collaboration beyond the DOD, engaging
          with the innovation ecosystem outside the military to leverage
          external expertise and industry insights for effective solution
          development.
        </p>
        <p>Questions? Email athena@npsfoundation.org</p>
      </div>
      <div className='form-wrapper'>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTabValue}
            onChange={handleTabChange}
            aria-label='request form tabs'
          >
            <Tab label='Individual' value={1} />
            <Tab label='Group' value={2} />
          </Tabs>
        </Box>
        {selectedTabValue === 1 && (
          <>
            <form onSubmit={formSubmission} onKeyDown={handleFormKeyDown}>
              <TextField
                label='First Name'
                name='firstName'
                fullWidth
                required
                margin='normal'
                variant='outlined'
                helperText='This will be used to create your Athena username in the format of [first name or forename initial][last name or surname].'
                onChange={handleInputChange}
              />
              <TextField
                label='Middle Name'
                name='middleName'
                fullWidth
                margin='normal'
                variant='outlined'
                helperText='This information will be used to search through Athena to match you with any documents you have authored.'
                onChange={handleInputChange}
              />
              <TextField
                label='Last Name or Surname'
                name='lastName'
                fullWidth
                required
                margin='normal'
                variant='outlined'
                helperText='This will be used to create your Athena username in the format of [first name or forename initial][last name or surname].'
                onChange={handleInputChange}
              />
              <TextField
                label="Maiden Name or 'Also Known As' Name"
                name='maidenName'
                fullWidth
                margin='normal'
                variant='outlined'
                helperText='Please list your maiden name or an Also Know As name if you have published a thesis or other documents under a different name. This will allow us to match your published documents to your published name.'
                onChange={handleInputChange}
              />
              <TextField
                inputRef={emailRef}
                label='Email Address'
                name='email'
                type='email'
                value={formData.email}
                required
                fullWidth
                margin='normal'
                variant='outlined'
                error={emailError}
                helperText={
                  emailError
                    ? emailErrorMessage
                    : 'Please use your official organization email for verification purposes.'
                }
                onChange={handleInputChange}
              />
              <TextField
                inputRef={passwordRef}
                label='Password'
                name='password'
                type='password'
                helperText='Password minimum eight characters, at least one uppercase letter, one lowercase letter and one number'
                required
                fullWidth
                autoFocus
                margin='normal'
                variant='outlined'
                error={passwordError}
                onChange={handleInputChange}
              />
              <FormControl
                autoFocus
                fullWidth
                margin='normal'
                ref={phoneNumberRef}
              >
                <PhoneInput
                  copyNumbersOnly
                  country={'us'}
                  inputStyle={{ width: '100%' }}
                  specialLabel='Phone Number'
                  containerStyle={{ marginBottom: '16px' }}
                  onChange={handlePhoneNumberChange}
                  placeholder='Phone number'
                  inputProps={{
                    required: false,
                  }}
                  enableSearch
                  isValid={() => {
                    if (!formSubmitted) {
                      return true;
                    } else {
                      if (isValid) {
                        return true;
                      } else {
                        return 'Enter a valid phone number';
                      }
                    }
                  }}
                />
              </FormControl>

              <FormControl fullWidth margin='normal'>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.userType}
                  onChange={handleDropdownChange}
                  label='User Type'
                  required
                >
                  {userTypesData
                    .filter((type) => type.UserTypeId !== 0)
                    .map((type) => (
                      <MenuItem key={type.NodeTypeId} value={type.Title}>
                        {type.Title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin='normal' required>
                <InputLabel>
                  Are you a current NPS student or an NPS alumni?
                </InputLabel>
                <Select
                  value={isAlumni === null ? '' : isAlumni ? 'yes' : 'no'}
                  onChange={(event) => {
                    const isAffiliated = event.target.value === 'yes';
                    setIsAlumni(isAffiliated);
                    setFormData({
                      ...formData,
                      isAlumni: isAffiliated,
                    });
                  }}
                  label='Are you a current NPS student or an NPS alumni?'
                >
                  <MenuItem value='yes'>Yes</MenuItem>
                  <MenuItem value='no'>No</MenuItem>
                </Select>
              </FormControl>
              {renderAdditionalFields()}
              <KeywordSearchDropdown
                keywordsData={keywordsData}
                getSelectedKeywords={getSelectedKeywords}
                label='Research Interests'
              />
              {researchInterestsError && (
                <Typography color='error' variant='body2' sx={{ mt: 1, ml: 2 }}>
                  Please select at least one research interest
                </Typography>
              )}
              <TextField
                label='Recommended By'
                name='recommendedBy'
                fullWidth
                margin='normal'
                variant='outlined'
                error={recommendedByError}
                onChange={handleInputChange}
                helperText={
                  recommendedByError
                    ? 'Use LastName,FirstName MiddleName format'
                    : 'Enter in LastName,FirstName MiddleName format (L,F M)'
                }
              />
              <Button
                type='submit'
                variant='contained'
                color='primary'
                style={{ marginTop: '20px' }}
              >
                Submit
              </Button>
              {!otpVerified ? OTPForm() : addUserDialog()}
            </form>
          </>
        )}
        {selectedTabValue === 2 && <GroupForm />}
      </div>
      <DuplicateUsersModal />
    </div>
  );
};

export default RequestForm;
